<template>
    <b-overlay no-center :show="!mainDashboards || (mainDashboards && mainDashboards.length == 0)" class="d-flex flex-column h-100 w-100">
        <b-card no-body header-class="p-0" class="d-flex flex-column mh-100 h-100 w-100 rounded-0">
            <!-- Liste des tableaux de bord principaux -->
            <b-tabs id="MainDashboards" card v-model="currentTab"
                :active-nav-item-class="'text-' + Variant" :nav-class="'text-' + Variant + ' text-dark p-0 pt-1'"
                :content-class="[mainDashboards && mainDashboards[currentTab] && mainDashboards[currentTab].EditMode ? 'edit' : '', 'bg-white', 'flex-grow-1', 'w-100', 'overflow-auto']"
                :active-tab-class="{'flex-grow-0': (currentTab == 0)}"
                class="d-flex flex-column mh-100 h-100 w-100">

                <!-- Prop lazy utilisée pour ne pas charger tous les onglets de dashboards dès le démarrage. Seul le 1er dashboard est toujours chargé. -->
                <b-tab v-for="mainDashboard in mainDashboards" :key="mainDashboard.Id" :title="mainDashboard.Name" :lazy="!(loadedDashboards.includes(mainDashboard.Id))"
                    @click="selectDashboard(mainDashboard)"
                    class="h-100 w-100">
                    
                    <!-- Rendu de l'onglet du tableau de bord -->
                    <template #title>
                        <b-form inline>
                            <!-- Nom du tableau de bord -->
                            <span>{{ mainDashboard.Name }}</span>

                            <!-- Menu d'actions sur l'onglet du tableau de bord -->
                            <b-dropdown v-if="mainDashboard.EditMode" :variant="'outline-' + Variant" no-caret no-flip
                                ref="dropdown" toggle-tag="div" toggle-class="border-0 p-0" menu-class="p-1" class="m-0 p-0">
                                <template #button-content>
                                    <b-icon icon="three-dots-vertical" />
                                </template>

                                <!-- Renommage du tableau de bord -->
                                <b-dropdown-item v-if="$store.getters['CheckAction']('Write', 'MainDashboard')" @click="$bvModal.show('tabRenameModal' + mainDashboard.Id)">{{ $t('Rename') }}</b-dropdown-item>
                                <!-- Suppression du tableau de bord -->
                                <b-dropdown-item v-if="$store.getters['CheckAction']('Delete', 'MainDashboard')" @click="remove(mainDashboard.Id)">{{ $t('Delete') }}</b-dropdown-item>
                            </b-dropdown>
                        </b-form>
                    </template>

                    <!-- Instance du tableau de bord principal pour l'onglet -->
                    <MainDashboard :PMainDashboard="mainDashboard" :ActiveDashboardId="mainDashboards && mainDashboards[currentTab] ? mainDashboards[currentTab].Id : -1" class="h-100 w-100" />
                </b-tab>

                <!-- Barre d'outils à droite de la liste des onglets -->
                <template #tabs-end>
                    <!-- Ajout d'un nouveau tableau de bord -->
                    <b-nav-item v-if="$store.getters['CheckAction']('Add', 'MainDashboard')" @click.prevent="newTab" v-b-tooltip.hover :title="$t('CreateDashboard')" role="button" link-classes="text-black-light">
                        <b-icon icon="plus-lg" font-scale="0.9" />
                    </b-nav-item>
                    
                    <!-- <b-button-toolbar justify key-nav aria-label="Barre d'outils avec des boutons" class="flex-grow-1 text-right"> -->
                    <b-row class="flex-grow-1 mr-0">
                        
                        <b-col align-self="center" class="px-0">
                            <!-- Portal de barre d'outils de boutons spécifiques au tableau de bord courant -->
                            <portal-target v-if="mainDashboards && mainDashboards.length > 0" name="MDashboardsToolbarPortal"
                                class="text-right" />
                        </b-col>

                        <b-col cols="auto" align-self="center" class="pl-1 pr-0">

                        </b-col>
                    </b-row>
                    <!-- </b-button-toolbar> -->     
                </template>

                <!-- Affiché quand aucun dashboard n'existe -->
                <template #empty>
                    
                </template>
            </b-tabs>
        </b-card>
        <!-- Overlay quand aucun tableau de bord n'existe -->
        <template #overlay>
            <Welcome :Variant="Variant" class="flex-grow-1">
                <!-- Suppression message de bienvenue par défaut-->
                <template v-slot:WelcomeMessage>
                    {{ $t('Welcome') }}
                </template>
                <!-- Slot de content supplémentaire pour MainDashboards ajouté au composant Welcome -->
                <template v-slot:MiscContent v-if="mainDashboards && mainDashboards.length == 0 && $store.getters['CheckAction']('Add', 'MainDashboard')">
                    <b-row align-v="stretch" class="pt-4 justify-content-center">
                        <b-col cols="auto">
                            <b-button @click.prevent="newTab" pill :variant="Variant">{{ $t('CreateDashboard') }}</b-button>
                        </b-col>
                    </b-row>
                </template>
            </Welcome>
        </template>
    </b-overlay>
</template>

<script lang="ts">
    import { Component, ProvideReactive, Vue } from 'vue-property-decorator';
    import { Getter } from 'vuex-class';
    import Welcome from '@/components/commons/Welcome.vue';
    import MainDashboard from './MainDashboards/MainDashboard.vue';
    import TMainDashboard from '@/types/Dashboard/MainDashboard/MainDashboard';
    import SaveModeEnum from '@/enums/SaveModeEnum';
    import MainWidget from '@/types/Dashboard/MainDashboard/MainWidget';
    import locales from "./locales/MainDashboards.json";

    /** Composant affichant la liste des onglets de tableau de bord général */
    @Component({
        components: {
            Welcome,
            MainDashboard
        },
        i18n: { messages : locales }
    })
    export default class MainDashboards extends Vue {
        /** Variant de couleur à appliquer sur les tableaux de bord */
        @ProvideReactive()
        private Variant: string = "primary";

        /** Liste des dashboards */
        @Getter('MainDashboardStore/GetMainDashboards')
        private mainDashboards!: TMainDashboard[];

        //#region Gestion chargement des onglets de dashboards
        /**
         * Liste des Id de MainDashboard chargés dans un onglet 
         * @description Pour tracker les onglets de dashboards qui ont déjà été ouvert et changer la prop Lazy à false si déjà ouvert ==> Evite que l'onglet se recharge avec tous les LoadResults de widget à chaque réouverture */
        private loadedDashboards: number[] = [];

        /** 
         * Sélection d'un tableau de bord
         * Utilisé pour conserver la liste des tableau de bord chargé pour les tabs en lazy + connaître si le tableau de bord courant est en mode Edition ou non ainsi que son Id
         * @param mainDashboard Tableau de bord sélectionné
         */
        private selectDashboard(mainDashboard: TMainDashboard) {
            console.debug("MainDashboards - selectDashboard - mainDashboard : ",  mainDashboard);

            /** Met à jour la liste des tableaux de bord chargés pour lazy */
            if(!this.loadedDashboards.includes(mainDashboard.Id))
                this.loadedDashboards.push(mainDashboard.Id)
        }
        //#endregion Gestion chargement des onglets de dashboards

        /** Index de l'onglet de dashboard actif dans le b-tabs */
        private currentTab = 0;

        /** Création d'un nouveau tableau de bord */
        private newTab() {
            const tabCounter = this.mainDashboards.length;

            // Créé un tableau de bord vide et l'enregistre
            this.$store.dispatch("MainDashboardStore/Save", {
                dashboard: { Id: tabCounter, Name: (this.$t('Dashboard').toString()) + " " + (tabCounter + 1), EditMode: true, Widgets: [] as MainWidget[] } as TMainDashboard, // "Tableau de bord "
                saveMode: SaveModeEnum.NewObject
            }).then(() => {
                /** Ouvre le nouvel onglet */
                this.$nextTick(() => {
                    this.$nextTick(() => {
                        requestAnimationFrame(() => {
                            this.currentTab = tabCounter ;
                        })
                    })
                });
            });
        }

        //#region Suppression d'un tableau de bord
        private remove(dashboardId) {
            this.$bvModal.msgBoxConfirm(this.$t('ConfirmDeleteMainDashboard').toString(), // 'Etes-vous sûr de vouloir supprimer ce tableau de bord principal ?
                { okVariant: 'danger', okTitle: this.$t('Yes').toString(), cancelTitle: this.$t('No').toString(), hideHeaderClose: false, centered: true, modalClass: 'text-center' })
                .then(value => {
                    if (value) {
                        console.debug("MainDashboard - remove() ");
                        this.$store.dispatch("MainDashboardStore/Delete", dashboardId).then(() => {
                            /** Supprime le dashboard de la liste des dashboards déjà chargé (sauf pour le premier qui ne doit pas être en lazy) */
                            let index = this.loadedDashboards.findIndex(x => x === dashboardId);
                            if (index > 0)
                                this.loadedDashboards.splice(index, 1);

                            this.$bvToast.toast(this.$t('SuccessDeleteMainDashboard').toString(), { // "Le tableau de bord a été supprimé avec succès !"
                                title: this.$t('Information').toString(),
                                autoHideDelay: 5000,
                                variant: "success",
                                solid: true,
                                appendToast: true,
                                toaster: "b-toaster-top-center"
                            });
                        });
                    }
                })
                .catch(err => {
                    console.error(err);
                    this.$bvToast.toast(this.$t('AnErrorOccured').toString(), { // "Une erreur s'est produite."
                        title: this.$t('Error').toString(),
                        autoHideDelay: 10000,
                        variant: "danger",
                        solid: true,
                        appendToast: true,
                        toaster: "b-toaster-top-center"
                    });
                });
        }
        //#endregion Suppression du tableau de bord

        async created() {
            // Chargement de la liste des dashboards
            await this.$store.dispatch("MainDashboardStore/LoadData").then(() => {
                /** Ajoute le premier dashboard dans la liste des dashboards déjà chargé pour gestion du mode Lazy (1er dashboard systématiquement chargé) */
                if (!this.mainDashboards || (this.mainDashboards && this.mainDashboards.length == 0))
                    this.$emit('NoMainDashboardsEvent');
                else
                    this.loadedDashboards.push(this.mainDashboards[0].Id);
            })
            .catch(err => {
                console.error(err);
                this.$bvToast.toast(this.$t('AnErrorOccured').toString(), { // "Une erreur s'est produite."
                    title: this.$t('LoadingDashboard').toString(), // "Chargement des tableaux de bord"
                    autoHideDelay: 10000,
                    variant: "danger",
                    solid: true,
                    appendToast: true,
                    toaster: "b-toaster-top-center"
                });
            });
        }
    }
</script>

<style scoped>
    :deep(.dropdown-item:hover) {
        background-color: var(--secondary-bg-subtle) !important;
    } 

    /** Change la propriété isolation pour corriger un pb sur le composant b-tab empêchant l'ouverture correcte de la dropdown de menu en mode edition */
    :deep(.nav-link:hover) {
        isolation: auto !important;
    }

    /** Met un gris légèrement plus foncé que $card-cap-bg (fichier default-theme.scss) sur le b-card au-dessus du b-tab */
    :deep(.card-header) {
        background-color: var(--gray) ;
    }
</style>